import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { getCachedText, preloadSiteContent } from '@/hooks/useSiteContent'

export default function Transformation() {
  const [, setTick] = useState(0)

  useEffect(() => {
    preloadSiteContent()
    const handler = () => setTick((n) => n + 1)
    window.addEventListener('site-content-updated', handler)
    return () => window.removeEventListener('site-content-updated', handler)
  }, [])

  const t = (key: string, fallback: string) => getCachedText('transformation', key, fallback)

  const parcours = [
    {
      titre: t('parcours_1_titre', 'Apaiser le mental'),
      sous: t('parcours_1_sous', 'Quand les pensées tournent en boucle.'),
      etapes: [
        t('parcours_1_etape_1', 'Reconnaître que le mental s\'agite, sans le juger.'),
        t('parcours_1_etape_2', 'Déposer l\'attention dans une respiration lente.'),
        t('parcours_1_etape_3', 'Laisser les pensées passer comme des nuages.'),
        t('parcours_1_etape_4', 'Revenir, encore, au souffle.')
      ]
    },
    {
      titre: t('parcours_2_titre', 'Retrouver de l\'énergie'),
      sous: t('parcours_2_sous', 'Quand le corps semble lourd ou éteint.'),
      etapes: [
        t('parcours_2_etape_1', 'Observer où se situe la fatigue : corps, émotions, mental.'),
        t('parcours_2_etape_2', 'Bouger doucement, sans objectif de performance.'),
        t('parcours_2_etape_3', 'Se rendre disponible à la lumière, à l\'air, au mouvement.'),
        t('parcours_2_etape_4', 'Accueillir ce qui revient, lentement.')
      ]
    },
    {
      titre: t('parcours_3_titre', 'Se recentrer'),
      sous: t('parcours_3_sous', 'Quand on se sent dispersé.'),
      etapes: [
        t('parcours_3_etape_1', 'Poser les pieds au sol, ressentir le contact.'),
        t('parcours_3_etape_2', 'Placer une main sur le cœur, l\'autre sur le ventre.'),
        t('parcours_3_etape_3', 'Respirer en conscience, sans modifier.'),
        t('parcours_3_etape_4', 'Revenir à l\'axe intérieur, à ce qui est là, maintenant.')
      ]
    },
    {
      titre: t('parcours_4_titre', 'Sortir du stress'),
      sous: t('parcours_4_sous', 'Quand la pression intérieure s\'intensifie.'),
      etapes: [
        t('parcours_4_etape_1', 'Nommer ce qui est présent, sans l\'amplifier.'),
        t('parcours_4_etape_2', 'Allonger l\'expiration, plus longue que l\'inspiration.'),
        t('parcours_4_etape_3', 'Relâcher consciemment les épaules, la mâchoire.'),
        t('parcours_4_etape_4', 'Offrir au corps un espace sans exigence.')
      ]
    }
  ]

  return (
    <div className="fade-in">
      {/* Intro */}
      <section className="px-6 py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs tracking-[0.4em] uppercase text-[#c9a96e] mb-8">
            {t('eyebrow', '— Troisième porte —')}
          </p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-[#f5f1ea] mb-10">
            {t('page_title', 'Transformation')}
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-10" />
          <p className="font-serif text-xl md:text-2xl text-[#f5f1ea]/80 leading-loose italic whitespace-pre-line">
            {t('page_intro', 'Des parcours simples,\npour accompagner ce qui traverse.')}
          </p>
        </div>
      </section>

      {/* Parcours */}
      <section className="px-6 pb-32">
        <div className="max-w-5xl mx-auto space-y-20">
          {parcours.map((parcours_item, i) => (
            <article
              key={i}
              className="group border border-[#c9a96e]/15 hover:border-[#c9a96e]/40 transition-all duration-700 p-8 md:p-16"
            >
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                <div className="lg:w-1/3 space-y-4">
                  <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/70">
                    Parcours {String(i + 1).padStart(2, '0')}
                  </p>
                  <h2 className="font-serif text-3xl md:text-4xl font-light text-[#f5f1ea]">
                    {parcours_item.titre}
                  </h2>
                  <p className="font-serif italic text-[#c9a96e]/80 text-lg">
                    {parcours_item.sous}
                  </p>
                </div>

                <div className="lg:w-2/3">
                  <div className="space-y-6">
                    {parcours_item.etapes.map((etape, j) => (
                      <div key={j} className="flex gap-6">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full border border-[#c9a96e]/40 flex items-center justify-center mt-1">
                          <span className="font-serif text-xs text-[#c9a96e]">{j + 1}</span>
                        </div>
                        <p className="text-base md:text-lg text-[#f5f1ea]/75 leading-loose font-light">
                          {etape}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Reminder */}
      <section className="px-6 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-2xl mx-auto text-center space-y-10">
          <p className="font-serif italic text-xl md:text-2xl text-[#f5f1ea]/75 leading-loose">
            {t('closing_1', 'Ces parcours ne sont ni des remèdes, ni des prescriptions.')}
          </p>
          <p className="text-base text-[#a8a39e] leading-loose font-light whitespace-pre-line">
            {t('closing_2', "Ce sont des propositions. Chacun trouve son rythme,\nson chemin, sa propre manière d'habiter l'instant.")}
          </p>
          <div className="pt-8">
            <Link
              to="/accompagnements"
              className="inline-block text-sm tracking-[0.3em] uppercase text-[#c9a96e] border-b border-[#c9a96e]/30 hover:border-[#c9a96e] pb-2 transition-all duration-500"
            >
              {t('closing_cta', 'Découvrir les accompagnements')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
